/**
 * 題の表記をそろえて見せる。元の題 (DB の文字列) は書き換えず、表示の側で整える。
 * 全角の括弧は「 (…)」の半角にし、和文と英数字の間には半角の空きを 1 つ入れる。和文どうしの間は詰めたままにする。
 * 和文と和文の間にある半角の空き 1 つは詰める (書き手が置いた「ゆめさく は」の空き)。空きが 2 つ以上の所は触らない (そのまま残す)。
 * 「18ヶ月」は「18 か月」にそろえる。「！！」のように並んだ感嘆符は 1 つにする。
 * URL (http から始まる語)、バッククォートの中、鉤括弧「」の中は、説明の対象の字なので触らない。
 */
const CJK = "\\u3040-\\u30ff\\u3400-\\u4dbf\\u4e00-\\u9fff";
// 欧文との間に空きを入れる和文。中黒 (・ U+30FB) は入れない (「ブラウザ・Cookie・セッション」が「ブラウザ・ Cookie ・セッション」になる)
const CJK_SPACED = "\\u3040-\\u30fa\\u30fc-\\u30ff\\u3400-\\u4dbf\\u4e00-\\u9fff";
const PROTECTED = /(?<!`)(`+)(?!`)[\s\S]*?(?<!`)\1(?!`)|https?:\/\/\S+|「[^」]*」/g;

// 和文の範囲 (全角の英数字は含めない。MarkdownContent の rehypeJoinCjkAcrossInline と同じ)
const WIDE = "\\u3000-\\u30ff\\u3400-\\u4dbf\\u4e00-\\u9fff\\uff00-\\uff0f\\uff1a-\\uff20\\uff3b-\\uff40\\uff5b-\\uffef";
const WIDE_GAP = new RegExp(`(?<=[${WIDE}]) (?=[${WIDE}])`, "g");
const MONTH = /(\d)\s?[ヶヵケ]月/g;
// 「Issue10件」のように英単語に数が直結して単位が続くときは、単語と数も離す (「Python3」のような名前は、単位が続かないので触らない)
const WORD_COUNT = /([A-Za-z]{3,})(\d+)(?=\s?(?:件|問|本|行|個|人|枚|語|項目))/g;

function tidyPlain(text: string): string {
  return text
    .replace(WIDE_GAP, "")
    .replace(MONTH, "$1か月")
    .replace(/([!！])[!！]+/g, "$1")
    // 和文のあとの半角の「!」は全角にそろえる (「ようこそ!」は「ようこそ！」)。「![画像]」の「!」と「!=」は触らない
    .replace(new RegExp(`([${CJK_SPACED}])!(?![\\[=])`, "g"), "$1！")
    .replace(WORD_COUNT, "$1 $2")
    .replace(/\s*（([^（）]*)）\s*/g, " ($1) ")
    .replace(new RegExp(`([${CJK_SPACED}])([A-Za-z0-9])`, "g"), "$1 $2")
    .replace(new RegExp(`([A-Za-z0-9])([${CJK_SPACED}])`, "g"), "$1 $2");
}

/**
 * Mattermost のチャンネル名の前の「#」と絵文字 (「#👋自己紹介」の「#👋」) を、説明の文から外す。
 * 画面の文は絵文字を使わず、チャンネルは「自己紹介チャンネル」の 1 つの書き方にそろえる (名前と「チャンネル」の間の空きも詰める)。
 * 本物の名前をそのまま見せる図とコピー用のテンプレートには使わない
 */
export function plainChannelNames(text: string): string {
  return text.replace(new RegExp(`(?<![A-Za-z0-9])[#＃][\\p{Extended_Pictographic}\\uFE0F\\u200D]*(?=[${CJK}])`, "gu"), "").replace(new RegExp(`([${CJK}]) (チャンネル)`, "g"), "$1$2");
}

/**
 * 守る所は、私用領域の 1 字の札に置き換えてから整え、あとで元に戻す。守る所を囲む括弧も整えられる。
 * channels を false にすると、「#」の直後の和文からも「#」を外さない (記事や投稿の「#ゆめさくエンジニア」のようなハッシュタグを残す)
 */
export function tidyText(title: string, { channels = true }: { channels?: boolean } = {}): string {
  const kept: string[] = [];
  const masked = title.replace(PROTECTED, (m) => {
    kept.push(m);
    return String.fromCharCode(0xe000 + kept.length - 1);
  });
  return tidyPlain(channels ? plainChannelNames(masked) : masked)
    .replace(/[\ue000-\uf8ff]/g, (c) => kept[c.charCodeAt(0) - 0xe000] ?? c)
    .trim();
}

/**
 * 項目の題を見せる形にする。種類の札が「宿題」と言っているので、題の先頭の「宿題」は外す (「宿題 PC環境…」の重なり)。
 * 外すと空になる題は、そのまま残す
 */
export function stepTitle(title: string, itemType?: string | null): string {
  const tidy = tidyText(title);
  if (itemType !== "homework") return tidy;
  const rest = tidy.replace(/^宿題[\s　:：\-ー・]*/, "").trim();
  return rest || tidy;
}

/**
 * 記事の種類の札。DB の種類名のうち英語のままのものを日本語にする (絞り込みの値は元の名前のまま使い、見せる字だけ替える)。
 * 「Tips」は「ヒント」
 */
export function categoryLabel(category: string): string {
  return category.trim().toLowerCase() === "tips" ? "ヒント" : category;
}

// Markdown の構文で、整えると壊れる所。リンクと画像の行き先 `](…)`、HTML のタグ、`<https://…>` の自動リンク
const MD_KEEP = /\]\((?:[^()\n]|\([^()\n]*\))*\)|<\/?[A-Za-z][^>\n]*>|<[A-Za-z][A-Za-z0-9+.-]*:[^>\s]*>/g;
// 強調と打ち消しの印。印の内側に空きが入ると、強調として読まれず「**」が字のまま出る
const MD_EMPHASIS = /\*+|_+|~~/g;
const MD_MARK_BASE = 0xf000;

/**
 * 1 行の本文を整える。構文の所は札に置き換えて守り、強調の印は「元の内側に空きが無ければ、整えたあとも空けない」を守る
 * (「**メモリ（RAM）**」が「**メモリ (RAM) **」になると強調が消える。空きは印の外へ移して「**メモリ (RAM)** を」にする)
 */
function tidyMarkdownText(body: string): string {
  const kept: string[] = [];
  const glues: ({ before: boolean; after: boolean } | null)[] = [];
  const hold = (m: string, glue: { before: boolean; after: boolean } | null) => {
    kept.push(m);
    glues.push(glue);
    return String.fromCharCode(MD_MARK_BASE + kept.length - 1);
  };
  const masked = body
    .replace(MD_KEEP, (m) => hold(m, null))
    .replace(MD_EMPHASIS, (m, offset: number, whole: string) =>
      hold(m, { before: offset > 0 && !/\s/.test(whole[offset - 1]), after: offset + m.length < whole.length && !/\s/.test(whole[offset + m.length]) }),
    );
  let tidy = tidyText(masked, { channels: false });
  // 整えて印の内側に入った空きは、印の外側へ移す (閉じる印は後ろ、開く印は前)。外側の空きは強調の境目として読まれる
  glues.forEach((glue, index) => {
    if (!glue) return;
    const mark = String.fromCharCode(MD_MARK_BASE + index);
    if (glue.before && new RegExp(`\\s${mark}`).test(tidy)) {
      tidy = tidy.replace(new RegExp(`\\s+${mark}(\\s*)(.?)`), (_, space: string, next: string) => `${mark}${space || (next ? " " : "")}${next}`);
    } else if (glue.after) {
      tidy = tidy.replace(new RegExp(`(.?)(\\s*)${mark}\\s+`), (_, prev: string, space: string) => `${prev}${space || (prev ? " " : "")}${mark}`);
    }
  });
  return tidy.replace(new RegExp(`[\\u${MD_MARK_BASE.toString(16)}-\\uf8ff]`, "g"), (c) => kept[c.charCodeAt(0) - MD_MARK_BASE] ?? c);
}

/**
 * Markdown の本文を、行ごとに整える。コードの囲み (``` と ~~~)、::: の囲みの行、HTML の行、字下げのコード、リンクの定義の行は触らない。
 * 行の中でも、リンクの行き先、タグ、強調の印の内側の境目は変えない (tidyMarkdownText)。
 * 行の頭の字下げと行末の空き (改行の印) は残す。tidyText は改行も空きとして畳むので、行をまたいで当てない
 */
export function tidyMarkdown(markdown: string): string {
  let fence: string | null = null;
  return markdown
    .split("\n")
    .map((line) => {
      const marker = /^\s*(```+|~~~+)/.exec(line)?.[1];
      if (marker) {
        if (fence === null) fence = marker[0];
        else if (marker[0] === fence) fence = null;
        return line;
      }
      if (fence !== null) return line;
      if (/^(\s*:{3}|\s*<|\t| {4}|\s*\[[^\]]+\]:)/.test(line)) return line;
      const head = /^\s*/.exec(line)?.[0] ?? "";
      const tail = /\s*$/.exec(line.slice(head.length))?.[0] ?? "";
      const body = line.slice(head.length, line.length - tail.length);
      return body ? head + tidyMarkdownText(body) + tail : line;
    })
    .join("\n");
}
